/**
 * About page copy, in one mutable object — the same arrangement as `home.ts`,
 * and for the same reason: the sections used module constants, which are fixed
 * before the CMS has answered.
 *
 * The values are what the page shipped with, and stand until the CMS overrides
 * them at startup.
 */

/** A run of the pull quote: set grey for emphasis, or not. */
export interface TextRun {
    text: string;
    grey: boolean;
}

export interface StatFigure {
    value: string;
    caption: string;
}

/** A programme card. `icon` names one of the marks in `components/about/OurPrograms.tsx`. */
export interface ProgrammeService {
    icon: string;
    title: string;
    body: string;
}

export interface PromoCard {
    /** Empty means the bundled photograph. */
    image: string;
    imageAlt: string;
    title: string;
    body: string;
}

export interface AboutContent {
    hero: {
        lead: string;
        body: string;
        ctaLabel: string;
        ctaHref: string;
        /** Empty means the bundled photograph / background. */
        photo: string;
        photoAlt: string;
        background: string;
    };
    who: {
        heading: string;
        highlight: string;
        body1: string;
        body2: string;
        quote: TextRun[];
        authorName: string;
        authorRole: string;
        authorAvatar: string;
    };
    missionVision: {
        missionParagraphs: string[];
        missionImage: string;
        missionImageAlt: string;
        visionParagraphs: string[];
        visionImage: string;
        visionImageAlt: string;
    };
    programmes: {
        intro: string;
        /** A figure ends with "+" to take the orange plus sign — see `counterParts`. */
        stats: StatFigure[];
        /** The dark panel's pill, then its grey and white heading lines. */
        eyebrow: string;
        titleLead: string;
        titleBold: string;
        services: ProgrammeService[];
        image: string;
        imageAlt: string;
    };
    team: { intro: string };
    volunteers: {
        titleLead: string;
        titleBold: string;
        lead: string;
        counterValue: string;
        counterLabel: string;
        wideCard: PromoCard;
        tallCard: PromoCard;
    };
    newsletter: { heading: string; body: string; subscriberCount: string };
    partners: { intro: string };
    volunteerForm: {
        heading: string;
        blurb: string;
        submitLabel: string;
        privacyNote: string;
        interestAreas: string[];
        askPhone: boolean;
        askMessage: boolean;
    };
    feedback: { heading: string; subheading: string; submitLabel: string };
}

export const ABOUT: AboutContent = {
    hero: {
        lead: 'A civic-technology organisation closing the distance between people and the justice they are owed.',
        body: 'We put lawyers where cases actually stall, publish the court data that makes delay impossible to ignore, and build tools that carry legal help the last mile — to the households formal systems rarely reach.',
        ctaLabel: 'Get in touch',
        ctaHref: '/help',
        photo: '',
        photoAlt: '',
        background: '',
    },
    who: {
        heading: 'About Us Citizens’ Gavel.',
        highlight: 'The delay is the injustice',
        body1: 'Citizens’ Gavel began with a simple observation: in Nigeria, the biggest obstacle to justice is rarely the law itself. It is delay. Cases stall for months over a missing file, an unfiled application or a hearing nobody attended — and the person waiting in a cell has no way to find out why, let alone fix it.',
        body2: 'So we built an organisation around closing that gap. We put lawyers where the delay happens, publish the court data that makes delay visible, and build technology that turns legal help from a privilege into something ordinary. Everything we do answers to one test: did a real case move, and did a real person get their life back?',
        quote: [
            { text: 'Justice delayed ', grey: false },
            { text: 'is not', grey: true },
            { text: ' a slogan here—it is ', grey: false },
            { text: 'someone’s eighth month', grey: true },
            { text: ' in a cell waiting for ', grey: false },
            { text: 'a file to be found.', grey: true },
        ],
        authorName: 'Nelson Olanipekun',
        authorRole: 'Founder Citizens’ Gavel',
        authorAvatar: '',
    },
    missionVision: {
        missionImage: '',
        missionImageAlt: '',
        visionImage: '',
        visionImageAlt: '',
        missionParagraphs: [
            'To make justice faster, fairer and easier to reach for people who cannot buy their way to it. We put lawyers where cases actually stall, publish the data that makes delay impossible to ignore, and build technology that carries legal help the last mile.',
            'We measure ourselves on movement, not activity: files unblocked, hearings held, people released, courts that publish records they used to withhold. If a case did not move, the work is not finished.',
        ],
        visionParagraphs: [
            'A Nigeria where the length of your detention does not depend on the size of your income, and where every court is answerable for the time it takes to deliver justice.',
            'We want the systems we build to become ordinary — legal help that people reach for as easily as they reach for a bank app, and court performance data so routine that publishing it is simply how a court operates.',
        ],
    },
    programmes: {
        intro: 'Our programmes confront injustice where it actually bites — in the cell, in the registry and in the data courts would rather not publish. Each one is built to move real cases, not to produce reports about them.',
        stats: [
            { value: '10M+', caption: 'People reached with legal help' },
            { value: '10M+', caption: 'Naira in legal fees waived' },
            { value: '10M+', caption: 'Days of court delay logged' },
        ],
        eyebrow: 'Our Services',
        titleLead: 'Our',
        titleBold: 'Programs',
        services: [
            {
                icon: 'Scale',
                title: 'Pro Bono Legal Aid',
                body: 'Free representation for people who cannot afford a lawyer',
            },
            {
                icon: 'Gavel',
                title: 'Police Duty Solicitor Scheme',
                body: 'Lawyers on hand at the station, before a case can stall',
            },
            {
                icon: 'ChartBar',
                title: 'Justice Delivery Index',
                body: 'Court performance measured, ranked and published openly',
            },
            {
                icon: 'Shield',
                title: 'SGBV Response',
                body: 'Survivor-led support, from first report through to trial',
            },
            {
                icon: 'Users',
                title: 'Community Paralegals',
                body: 'Trusted first responders where formal help rarely reaches',
            },
            {
                icon: 'Handshake',
                title: 'Justice Tech Lab',
                body: 'Open tools built with courts, lawyers and civic partners',
            },
        ],
        image: '',
        imageAlt: '',
    },
    team: {
        intro: 'Lawyers, researchers, engineers and organisers — brought together by the conviction that a justice system is only as good as the people it actually reaches.',
    },
    volunteers: {
        titleLead: 'Want to',
        titleBold: 'Our Volunteers',
        lead: '',
        counterValue: '200+',
        counterLabel: 'Volunteers Needed',
        wideCard: {
            image: '',
            imageAlt: '',
            title: 'Give a few hours a month',
            body: 'Lawyers take on pro bono files. Everyone else helps with research, translation, outreach and data.',
        },
        tallCard: {
            image: '',
            imageAlt: '',
            title: 'You don’t need a law degree to help.',
            body: 'Volunteers sit at the centre of this work — in courtrooms, in communities and behind the data.',
        },
    },
    newsletter: {
        heading: 'Our Newsletter',
        body: 'One email a month: the cases we moved, the courts we ranked and the tools we shipped. No noise, no fundraising drives.',
        subscriberCount: '33k+',
    },
    partners: {
        intro: 'None of this works alone. We build alongside courts, bar associations, community organisations and funders who are willing to open their data, share their case load and be measured on the result.',
    },
    volunteerForm: {
        heading: 'Join the volunteers',
        blurb: 'You don’t need a law degree. Tell us where you can help and we will be in touch.',
        submitLabel: 'Send my details',
        privacyNote: 'We use these details only to contact you about volunteering.',
        interestAreas: [
            'Pro bono legal work',
            'Research & data',
            'Court observation',
            'Community outreach',
            'Translation',
            'Technology & product',
        ],
        askPhone: true,
        askMessage: true,
    },
    feedback: { heading: 'Got Any Feedback?', subheading: 'Send all your feedback below', submitLabel: 'Submit' },
};

/** "Our Newsletter" → grey "Our " + white "Newsletter": the first word is the lead-in. */
export function headingParts(heading: string): { lead: string; bold: string } {
    const words = heading.trim().split(/\s+/).filter(Boolean);
    if (words.length <= 1) return { lead: '', bold: words[0] ?? '' };
    return { lead: `${words[0]} `, bold: words.slice(1).join(' ') };
}

/** "33k+" → "33k" and "+", so the plus can take the accent colour. */
export function counterParts(value: string): { figure: string; plus: string } {
    const m = /^(.*?)(\+?)$/.exec(value.trim());
    return { figure: m?.[1] ?? value, plus: m?.[2] ?? '' };
}
